'use client';

import { useEffect, useRef } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { ensureSession } from '@/lib/api';

export function UserDataHydration() {
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const token = useStore(state => state.webAuth.customToken);

  // Get all the actions we need to refresh user data
  const profileSync = useStore(state => state.profile?.syncProfile);
  const fetchExplorerData = useStore(state => state.explorer?.fetchExplorerData);
  const fetchReviewerInfo = useStore(state => state.reviewer?.fetchReviewerInfo);
  const fetchSavedUnits = useStore(state => state.savedUnits?.fetchSavedUnits);
  const setCurrentUserScheduledUnits = useStore(state => state.chat?.setCurrentUserScheduledUnits);
  // Get clear actions for logout
  const clearProfile = useStore(state => state.profile?.clearProfile);
  const clearExplorerData = useStore(state => state.explorer?.clearExplorerData);
  const clearReviewerInfo = useStore(state => state.reviewer?.clearReviewerInfo);
  const clearSavedUnits = useStore(state => state.savedUnits?.clearSavedUnits);
  const clearMessages = useStore(state => state.chat?.clearChatData);
  const sessionHydrated = useStore(state => state.webAuth.sessionHydrated);
  /**
   * Refresh all current user data
   * Called when user logs in or token changes
   */
  const refreshCurrentUserData = async () => {
    if (!token || !isLoggedIn) return;
    if (!(await ensureSession())) return;

    try {
      // Execute all refresh calls in parallel
      await Promise.all([
        // Sync user profile
        profileSync ? profileSync() : Promise.resolve(),

        // Fetch explorer data
        fetchExplorerData ? fetchExplorerData() : Promise.resolve(),

        // Fetch reviewer info
        fetchReviewerInfo ? fetchReviewerInfo() : Promise.resolve(),

        // Fetch saved units
        fetchSavedUnits ? fetchSavedUnits() : Promise.resolve(),

        // Fetch scheduled units
        setCurrentUserScheduledUnits ? setCurrentUserScheduledUnits() : Promise.resolve(),

        // Add more API calls here as needed:
        // - Chat/scheduled units
        // - Saved units
        // - Applications
        // - City resources
        // - etc.
      ]);
    } catch (error) {
      // Silent error - individual API failures shouldn't block auth
      console.error('❌ Error refreshing user data:', error);
    }
  };

  /**
   * Clear all user data on logout
   */
  const clearAllUserData = () => {
    // Clear all user-related data
    clearProfile?.();
    clearExplorerData?.();
    clearReviewerInfo?.();
    clearSavedUnits?.();
    clearMessages?.();
  };

  useEffect(() => {
    if (!sessionHydrated) return;
    if (isLoggedIn && token) {
      // User just logged in - refresh all data
      refreshCurrentUserData();
    } else if (!isLoggedIn) {
      // User just logged out - clear all data
      clearAllUserData();
    }
  }, [isLoggedIn, token, sessionHydrated]);

  return null;
}
